import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CustomField } from '../models/custom-fields.model';
import { CustomFieldRendererService } from './custom-field-renderer.service';
import { ObservationCategory, ObservationCategoryOption } from '../models/observations.model';

/**
 * Unified Observation Custom Fields Service
 *
 * This service provides a standardized way to handle custom fields across all
 * observation creation components (document popup, floor plan, add dialog).
 *
 * It eliminates code duplication and ensures consistent behavior across all
 * observation creation flows.
 */
@Injectable({
  providedIn: 'root',
})
export class ObservationCustomFieldsService {
  private readonly NWC_ATTACHMENT_REQUIRED_TYPES = [
    'NCR',
    'MIR_LOGS',
    'WIR_LOGS',
    'MIR LOGS',
    'WIR LOGS',
  ];

  // Category types that are restricted to NWC clients only
  private readonly NWC_CLIENT_ONLY_CATEGORY_TYPES = [
    'RFI',
    'MIR',
    'WIR',
    'NCR',
    'URGE',
    'MIR_LOGS',
    'WIR_LOGS',
    'MIR LOGS',
    'WIR LOGS',
    'SNAG',
    'ISSUE',
  ];

  private readonly NWC_CLIENT_ONLY_FILTER_TYPES = [
    'RFI',
    'MIR',
    'WIR',
    'NCR',
    'URGE',
    'MIR_LOGS',
    'WIR_LOGS',
  ];

  // Observation types that should NOT display Record No field
  private readonly TYPES_WITHOUT_RECORD_NO = ['GENERAL', 'DS', 'NOTE', 'SOR', 'SAR'];

  // Observation types that should NOT display Sequence No field
  private readonly TYPES_WITHOUT_SEQUENCE_NO = [
    'SNAG',
    'ISSUE',
    'GENERAL',
    'DS',
    'NOTE',
    'SOR',
    'SAR',
  ];

  // Observation types that should display Rev# field
  private readonly TYPES_WITH_TOTAL_RECORD_REV = [
    'MIR',
    'WIR',
    'MIR_LOGS',
    'WIR_LOGS',
    'MIR LOGS',
    'WIR LOGS',
  ];

  // Parent observation types that should display "Total Record Rev#" label
  private readonly TYPES_WITH_FULL_RECORD_REV_LABEL = ['MIR', 'WIR'];

  // LOGS observation types that should display "Rev#" label
  private readonly TYPES_WITH_SHORT_REV_LABEL = ['MIR_LOGS', 'WIR_LOGS', 'MIR LOGS', 'WIR LOGS'];

  // Observation types that allow status editing for NWC clients
  private readonly STATUS_EDITABLE_TYPES_FOR_NWC = [
    'ISSUE',
    'SNAG',
    'GENERAL',
    'NOTE',
    'DS',
    'SAR',
    'SOR',
  ];

  // MIR_LOGS non-editable fields configuration (for creation forms)
  // These fields will be populated from the parent observation
  private readonly MIR_LOGS_DISABLED_FIELDS = ['description', 'categories'];

  // WIR_LOGS non-editable fields configuration (for creation forms)
  // These fields will be populated from the parent observation
  private readonly WIR_LOGS_DISABLED_FIELDS = ['categories'];

  constructor(private customFieldRenderer: CustomFieldRendererService) {}

  /**
   * Setup custom fields for the current observation type
   * This method standardizes the custom fields setup across all components
   *
   * @param observationType - The observation type to load custom fields for
   * @param form - The form group to add custom field controls to
   * @param customFields$ - BehaviorSubject to emit custom fields
   * @param isLoadingCustomFields$ - BehaviorSubject to control loading state
   * @param onError - Optional error callback
   * @returns Observable<CustomField[]> - Observable that emits the loaded custom fields
   */
  setupCustomFields(
    observationType: string,
    form: FormGroup,
    customFields$: BehaviorSubject<CustomField[]>,
    isLoadingCustomFields$: BehaviorSubject<boolean>,
    onError?: (error: any) => void,
    observationId?: string | number | null,
  ): Observable<CustomField[]> {
    isLoadingCustomFields$.next(true);

    // Apply field disabling for LOGS observation types
    // This ensures fields are disabled even on initial form load
    this.applyLogsFieldsDisabling(observationType, form);

    const customFieldsObservable = this.customFieldRenderer.loadCustomFieldsForObservationType(
      observationType,
      form,
      async (fields: CustomField[]) => {
        customFields$.next(fields);
        isLoadingCustomFields$.next(false);
      },
      (error) => {
        console.error('Error loading custom fields:', error);
        customFields$.next([]);
        isLoadingCustomFields$.next(false);
        if (onError) {
          onError(error);
        }
      },
      observationId,
    );

    return customFieldsObservable;
  }

  /**
   * Get custom field values from the form
   * Standardized method to extract custom field values
   *
   * @param form - The form group containing custom field controls
   * @param customFields - Array of custom fields to extract values for
   * @returns Object containing field name-value pairs
   */
  getCustomFieldValues(form: FormGroup, customFields: CustomField[]): { [key: string]: any } {
    return this.customFieldRenderer.getCustomFieldValues(form, customFields);
  }

  /**
   * Track by function for custom fields
   * Standardized trackBy function for ngFor optimization
   *
   * @param index - Index of the field
   * @param field - The custom field object
   * @returns Unique identifier for the field
   */
  trackByField(index: number, field: CustomField): string {
    return field.name;
  }

  /**
   * Check if a value is a date value
   * Standardized date detection logic
   *
   * @param value - The value to check
   * @returns True if the value is a date, false otherwise
   */
  isDateValue(value: any): boolean {
    if (!value) return false;

    // Check if it's a Date object
    if (value instanceof Date) return true;

    // Check if it's a string that looks like a date
    if (typeof value === 'string') {
      const datePattern = /^\d{4}-\d{2}-\d{2}/; // YYYY-MM-DD
      const datetimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/; // YYYY-MM-DDTHH:MM
      const datePatternWithTimezone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/; // YYYY-MM-DDTHH:MM:SS.SSS
      return (
        datePattern.test(value) ||
        datetimePattern.test(value) ||
        datePatternWithTimezone.test(value)
      );
    }

    return false;
  }

  /**
   * Process form data for submission
   * Standardized method to handle custom fields in form submission
   *
   * @param form - The form group
   * @param formData - The FormData object to append to
   * @param customFields - Array of custom fields
   */
  processFormDataForSubmission(
    form: FormGroup,
    formData: FormData,
    customFields: CustomField[],
  ): void {
    const customFieldsByName = new Map(customFields.map((field) => [field.name, field]));

    Object.keys(form.controls).forEach((control) => {
      const controlItem = form.controls[control]?.value;

      if (this.shouldSkipOptionalEmptyCustomField(control, controlItem, customFieldsByName)) {
        return;
      }

      if (
        (typeof controlItem === 'object' && typeof controlItem?.getMonth !== 'function') ||
        control === 'screen_shot'
      ) {
        // Skip complex objects and screen_shot (handled separately)
      } else if (this.isDateValue(controlItem)) {
        formData.append(control, this.customFieldRenderer.formatDateForApi(controlItem));
      } else {
        let valueToAppend = form.controls[control]?.value;

        // Decode HTML entities for rich-text fields when they appear HTML-escaped
        if (
          typeof valueToAppend === 'string' &&
          (control === 'description' || control === 'recommended_corrective_action') &&
          /&lt;|&gt;|&amp;|&quot;|&#39;/.test(valueToAppend)
        ) {
          const textarea = document.createElement('textarea');
          textarea.innerHTML = valueToAppend;
          valueToAppend = textarea.value;
        }

        formData.append(control, valueToAppend);
      }
    });
  }

  private shouldSkipOptionalEmptyCustomField(
    control: string,
    value: any,
    customFieldsByName: Map<string, CustomField>,
  ): boolean {
    const customField = customFieldsByName.get(control);

    if (!customField || customField.required === true) {
      return false;
    }

    return (
      value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
    );
  }

  /**
   * Initialize custom fields properties
   * Creates the standard BehaviorSubjects used across all components
   *
   * @returns Object containing customFields$ and isLoadingCustomFields$
   */
  initializeCustomFieldsProperties(): {
    customFields$: BehaviorSubject<CustomField[]>;
    isLoadingCustomFields$: BehaviorSubject<boolean>;
  } {
    return {
      customFields$: new BehaviorSubject<CustomField[]>([]),
      isLoadingCustomFields$: new BehaviorSubject<boolean>(false),
    };
  }

  /**
   * Validate custom fields in form
   * Standardized validation for custom fields
   *
   * @param form - The form group
   * @param customFields - Array of custom fields
   * @returns Object containing validation result and errors
   */
  validateCustomFields(
    form: FormGroup,
    customFields: CustomField[],
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    customFields.forEach((field) => {
      const control = form.get(field.name);
      if (control && control.invalid) {
        const fieldErrors = this.customFieldRenderer.validateFieldValue(field, control.value);
        errors.push(...fieldErrors.errors);
      }
    });

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  normalizeObservationCategories(categories?: ObservationCategory[] | null): ObservationCategory[] {
    return Array.isArray(categories) ? categories : [];
  }

  buildObservationCategoryOptions(
    categories: ObservationCategory[],
    activeLang: string,
  ): ObservationCategoryOption[] {
    return this.normalizeObservationCategories(categories).map((category) => ({
      label: activeLang === 'ar' ? category.category_name_ar : category.category_name_en,
      value: category.id,
      children: category.children,
    }));
  }

  resetObservationCategoryState(
    form: FormGroup,
    isNwcClient: boolean,
    resetParentCategories = false,
  ): void {
    if (form.contains('child_categories')) {
      form.get('child_categories')?.setValue(isNwcClient ? null : [], { emitEvent: false });
    }

    if (resetParentCategories && form.contains('categories')) {
      form.get('categories')?.setValue(isNwcClient ? null : [], { emitEvent: false });
    }

    form.get('child_categories')?.updateValueAndValidity({ emitEvent: false });
  }

  syncSelectedObservationCategories(
    form: FormGroup,
    categories: ObservationCategory[],
    isNwcClient: boolean,
  ): void {
    const categoriesControl = form.get('categories');
    if (!categoriesControl) {
      return;
    }

    const selectedCategories = categoriesControl.value;
    const availableCategories = this.normalizeObservationCategories(categories);

    if (isNwcClient) {
      if (selectedCategories && !this.categoryExists(availableCategories, selectedCategories)) {
        categoriesControl.setValue(null, { emitEvent: false });
      }
      return;
    }

    const selectedCategoryIds = Array.isArray(selectedCategories)
      ? selectedCategories
      : selectedCategories
      ? [selectedCategories]
      : [];

    const validSelectedCategoryIds = selectedCategoryIds.filter((categoryId) =>
      this.categoryExists(availableCategories, categoryId),
    );

    if (
      selectedCategoryIds.length !== validSelectedCategoryIds.length ||
      !Array.isArray(selectedCategories)
    ) {
      categoriesControl.setValue(validSelectedCategoryIds, { emitEvent: false });
    }
  }

  getObservationChildCategories(
    categories: ObservationCategory[],
    selectedCategories: unknown,
    isNwcClient: boolean,
  ): ObservationCategory[] {
    const availableCategories = this.normalizeObservationCategories(categories);
    const selectedCategoryIds = isNwcClient
      ? selectedCategories
        ? [selectedCategories]
        : []
      : Array.isArray(selectedCategories)
      ? selectedCategories
      : selectedCategories
      ? [selectedCategories]
      : [];

    const childCategoriesById = new Map<string, ObservationCategory>();

    selectedCategoryIds.forEach((categoryId) => {
      const selectedCategory = availableCategories.find((category) =>
        this.sameCategoryId(category.id, categoryId),
      );

      selectedCategory?.children?.forEach((childCategory) => {
        childCategoriesById.set(String(childCategory.id), childCategory);
      });
    });

    return Array.from(childCategoriesById.values());
  }

  shouldShowObservationSubcategoryField(
    form: FormGroup | null | undefined,
    childCategories: ObservationCategory[],
    isNwcClient: boolean,
    categories?: ObservationCategory[],
  ): boolean {
    if (!form) {
      return false;
    }

    const typeControl = form.get('type');
    if (typeControl && !typeControl.value) {
      return false;
    }

    const selectedCategories = form.get('categories')?.value;
    const matchingChildCategories = categories
      ? this.getObservationChildCategories(categories, selectedCategories, isNwcClient)
      : this.normalizeObservationCategories(childCategories);

    if (matchingChildCategories.length === 0) {
      return false;
    }

    return isNwcClient
      ? !!selectedCategories
      : Array.isArray(selectedCategories) && selectedCategories.length > 0;
  }

  /**
   * Reset custom fields
   * Standardized method to reset custom fields state
   *
   * @param form - The form group
   * @param customFields$ - BehaviorSubject for custom fields
   * @param isLoadingCustomFields$ - BehaviorSubject for loading state
   */
  resetCustomFields(
    form: FormGroup,
    customFields$: BehaviorSubject<CustomField[]>,
    isLoadingCustomFields$: BehaviorSubject<boolean>,
  ): void {
    // Use the comprehensive cleanup method to remove ALL custom fields state
    // This includes registered fields, synthetic fields from related_fields,
    // lock counts, parent-to-targets mappings, and external adapters
    const customFields = customFields$.value;
    this.customFieldRenderer.clearAllCustomFieldsState(form, customFields);

    // Reset state
    customFields$.next([]);
    isLoadingCustomFields$.next(false);
  }

  /**
   * Handle observation type change
   * This method handles both custom fields and categories when observation type changes
   * Standardized method for all components
   *
   * @param observationType - The new observation type
   * @param form - The form group
   * @param customFields$ - BehaviorSubject for custom fields
   * @param isLoadingCustomFields$ - BehaviorSubject for loading state
   * @param onCategoriesLoaded - Optional callback for when categories are loaded
   * @param onError - Optional error callback
   * @param isNwcClient - Optional flag to indicate if the client is NWC (only reset categories for NWC clients)
   */
  handleObservationTypeChange(
    observationType: string,
    form: FormGroup,
    customFields$: BehaviorSubject<CustomField[]>,
    isLoadingCustomFields$: BehaviorSubject<boolean>,
    onCategoriesLoaded?: (categories: any[]) => void,
    onError?: (error: any) => void,
    isNwcClient?: boolean,
  ): void {
    // console.log('🔄 [handleObservationTypeChange] Changing observation type to:', observationType);

    // Reset child categories when type changes
    if (form.contains('child_categories')) {
      form.get('child_categories')?.setValue(null);
    }

    if (form.contains('sub_categories')) {
      form.get('sub_categories')?.setValue(null);
    }

    if (form.contains('subcategories')) {
      form.get('subcategories')?.setValue(null);
    }

    // Only reset categories for NWC clients
    // For non-NWC clients, preserve the selected category when changing observation type
    if (isNwcClient && form.contains('categories')) {
      form.get('categories')?.setValue(null);
    }

    // Use the comprehensive cleanup method to remove ALL custom fields state
    // This includes registered fields, synthetic fields from related_fields,
    // lock counts, parent-to-targets mappings, and external adapters
    const currentCustomFields = customFields$.value;
    // console.log(
    //   '🔄 [handleObservationTypeChange] Current custom fields before cleanup:',
    //   currentCustomFields?.length ?? 0,
    //   currentCustomFields?.map((f) => f.name) ?? [],
    // );

    this.customFieldRenderer.clearAllCustomFieldsState(form, currentCustomFields);

    // Disable specific fields for LOGS observation types (MIR_LOGS, WIR_LOGS)
    // These fields will be populated from the parent observation
    this.applyLogsFieldsDisabling(observationType, form);

    this.setupCustomFields(
      observationType,
      form,
      customFields$,
      isLoadingCustomFields$,
      onError,
    ).subscribe();
  }

  /**
   * Disable specific fields for LOGS observation types
   * For MIR_LOGS: Disable description and categories
   * For WIR_LOGS: Disable categories only
   * These fields will be populated from the parent observation
   *
   * @param observationType - The observation type
   * @param form - The form group
   */
  private applyLogsFieldsDisabling(observationType: string, form: FormGroup): void {
    const normalizedType = observationType?.toUpperCase().trim();

    // First, re-enable all potentially disabled fields from previous type
    // This ensures fields are enabled when switching away from LOGS types
    ['description', 'categories'].forEach((fieldName) => {
      const control = form.get(fieldName);
      if (control && control.disabled) {
        control.enable({ emitEvent: true }); // Emit event so Quill editors can react
      }
    });

    // Then disable fields based on the current observation type
    let fieldsToDisable: string[] = [];

    if (normalizedType === 'MIR_LOGS') {
      fieldsToDisable = this.MIR_LOGS_DISABLED_FIELDS;
    } else if (normalizedType === 'WIR_LOGS') {
      fieldsToDisable = this.WIR_LOGS_DISABLED_FIELDS;
    }

    // Disable the specified fields
    fieldsToDisable.forEach((fieldName) => {
      const control = form.get(fieldName);
      if (control) {
        control.disable({ emitEvent: true }); // Emit event so Quill editors can react
        control.setValue(null, { emitEvent: false });
      }
    });
  }

  /**
   * Check if description field should be disabled for the given observation type
   * Used by components to disable Quill editors visually
   *
   * @param observationType - The observation type to check
   * @returns True if description should be disabled
   */
  shouldDisableDescription(observationType: string): boolean {
    const normalizedType = observationType?.toUpperCase().trim();
    return normalizedType === 'MIR_LOGS';
  }

  /**
   * Check if categories field should be disabled for the given observation type
   *
   * @param observationType - The observation type to check
   * @returns True if categories should be disabled
   */
  shouldDisableCategories(observationType: string): boolean {
    const normalizedType = observationType?.toUpperCase().trim();
    return normalizedType === 'MIR_LOGS' || normalizedType === 'WIR_LOGS';
  }

  requiresAttachmentForCreation(
    observationType: string | null | undefined,
    isNwcClient: boolean,
  ): boolean {
    if (!isNwcClient || !observationType) {
      return false;
    }

    return this.NWC_ATTACHMENT_REQUIRED_TYPES.includes(observationType.toUpperCase().trim());
  }

  validateAttachmentsForCreation(
    observationType: string | null | undefined,
    isNwcClient: boolean,
    attachments: File[] | null | undefined,
    isDraft: boolean,
  ): { valid: boolean; errorKey: string | null } {
    if (isDraft || !this.requiresAttachmentForCreation(observationType, isNwcClient)) {
      return { valid: true, errorKey: null };
    }

    if (!attachments?.length) {
      return {
        valid: false,
        errorKey: 'observation.attachments_required_for_type',
      };
    }

    return { valid: true, errorKey: null };
  }

  /**
   * Check if a category type is restricted to NWC clients only
   *
   * @param categoryType - The category type to check
   * @returns True if the category type is restricted to NWC clients only
   */
  isCategoryTypeUpdatable(categoryType: string): boolean {
    return this.NWC_CLIENT_ONLY_CATEGORY_TYPES.includes(categoryType.toUpperCase());
  }

  /**
   * Get the list of NWC-only category types
   *
   * @returns Array of category types restricted to NWC clients
   */
  getNWCOnlyCategoryTypes(): string[] {
    return [...this.NWC_CLIENT_ONLY_CATEGORY_TYPES];
  }

  /**
   * Get the list of NWC-only filter types
   *
   * @returns Array of filter types restricted to NWC clients
   */
  getNWCOnlyFilterTypes(): string[] {
    return [...this.NWC_CLIENT_ONLY_FILTER_TYPES];
  }

  /**
   * Check if Record No should be displayed for the given observation type
   *
   * @param observationType - The observation type or type_label to check
   * @returns True if Record No should be displayed, false otherwise
   */
  shouldDisplayRecordNo(observationType: string): boolean {
    if (!observationType) return false;
    const normalizedType = observationType.toUpperCase().trim();
    return !this.TYPES_WITHOUT_RECORD_NO.includes(normalizedType);
  }

  /**
   * Check if Sequence No should be displayed for the given observation type
   *
   * @param observationType - The observation type or type_label to check
   * @returns True if Sequence No should be displayed, false otherwise
   */
  shouldDisplaySequenceNo(observationType: string): boolean {
    if (!observationType) return false;
    const normalizedType = observationType.toUpperCase().trim();
    return !this.TYPES_WITHOUT_SEQUENCE_NO.includes(normalizedType);
  }

  /**
   * Check if Total Record Rev# should be displayed for the given observation type
   * Only displays for MIR and WIR observation types
   *
   * @param observationType - The observation type or type_label to check
   * @returns True if Total Record Rev# should be displayed, false otherwise
   */
  shouldDisplayTotalRecordRev(observationType: string): boolean {
    if (!observationType) return false;
    const normalizedType = observationType.toUpperCase().trim();
    return this.TYPES_WITH_TOTAL_RECORD_REV.includes(normalizedType);
  }

  /**
   * Check if the observation type should show "Total Record Rev#" label
   * Returns true for MIR and WIR (parent types)
   *
   * @param observationType - The observation type or type_label to check
   * @returns True if "Total Record Rev#" label should be shown
   */
  shouldShowFullRecordRevLabel(observationType: string): boolean {
    if (!observationType) return false;
    const normalizedType = observationType.toUpperCase().trim();
    return this.TYPES_WITH_FULL_RECORD_REV_LABEL.includes(normalizedType);
  }

  /**
   * Check if the observation type should show "Rev#" label
   * Returns true for MIR_LOGS and WIR_LOGS
   *
   * @param observationType - The observation type or type_label to check
   * @returns True if "Rev#" label should be shown
   */
  shouldShowShortRevLabel(observationType: string): boolean {
    if (!observationType) return false;
    const normalizedType = observationType.toUpperCase().trim();
    return this.TYPES_WITH_SHORT_REV_LABEL.includes(normalizedType);
  }

  /**
   * Check if status editing should be disabled for NWC clients
   * Status editing is allowed only for specific observation types for NWC clients
   *
   * @param observationType - The observation type or type_label to check
   * @param isNwcClient - Whether the current client is an NWC client
   * @returns True if status editing should be disabled, false otherwise
   */
  isStatusEditingDisabledForNWC(observationType: string, isNwcClient: boolean): boolean {
    if (!isNwcClient) {
      return false; // Not an NWC client, don't disable
    }

    if (!observationType) {
      return true; // No type specified for NWC client, disable by default
    }

    // NWC client: allow editing only for specific observation types
    const normalizedType = observationType.toUpperCase().trim();
    return !this.STATUS_EDITABLE_TYPES_FOR_NWC.includes(normalizedType);
  }

  private categoryExists(categories: ObservationCategory[], categoryId: unknown): boolean {
    return categories.some((category) => this.sameCategoryId(category.id, categoryId));
  }

  private sameCategoryId(firstCategoryId: unknown, secondCategoryId: unknown): boolean {
    return String(firstCategoryId) === String(secondCategoryId);
  }
}
